import clsx from 'clsx';
import { useField, useFormikContext } from 'formik';
import type { FC } from 'react';
import { useCallback } from 'react';
import { RADIO_GROUP_INPUT_TEST_IDS } from './constants';
import InputError from '~/components/feedback/InputError';
import type { IDropdownOption, IRadioInput } from '~/types/inputs';

const RadioGroup: FC<IRadioInput> = ({ name, label, width, type, options, ...rest }) => {
	const [field, meta] = useField(name);
	const { submitCount } = useFormikContext();
	const isError = (meta.touched || submitCount > 0) && !!meta.error;

	const renderOption = useCallback(
		(option: IDropdownOption) => {
			const optionId = `${field.name}_${option.value}`;
			const isSelected = option.value === field.value;

			return (
				<div className="relative flex items-center mb-3 last:mb-0" key={optionId}>
					<div className="relative cursor-pointer self-start">
						<input
							name={field.name}
							onChange={field.onChange}
							value={option.value}
							type="radio"
							className="cursor-pointer peer absolute opacity-0 w-5 h-5"
							id={optionId}
							checked={isSelected}
							data-testid={RADIO_GROUP_INPUT_TEST_IDS.INPUT}
						/>
						<div
							className={clsx(
								'radio-pseudo-input cursor-pointer w-5 h-5 rounded-full flex items-center justify-center text-transparent border-2 peer-checked:border-secondary-text peer-focus:ring-4',
								'after:w-1/2 after:h-1/2 after:rounded-full border-input-inactive',
							)}
						/>
					</div>
					<label
						htmlFor={optionId}
						className="cursor-pointer ml-3 text-sm font-medium form-label text-left font-large-body font-weight-body"
					>
						{option.label}
					</label>
				</div>
			);
		},
		[field, name, rest, type],
	);

	return (
		<fieldset data-testid={RADIO_GROUP_INPUT_TEST_IDS.ROOT} className="relative col-span-6">
			<legend
				className="font-subheading text-sm mb-3"
				data-testid={RADIO_GROUP_INPUT_TEST_IDS.LABEL}
			>
				{label}
			</legend>
			{options.map(renderOption)}
			<InputError name={name} isError={isError}>
				{meta.error}
			</InputError>
		</fieldset>
	);
};

export default RadioGroup;
